import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import service, {
  CampanhaComunicacao,
  CampanhaPayload,
  CanalCampanha,
  FiltrosSegmentacao,
  PreviewResult,
  StatusCampanhaComunicacao,
} from '../../services/marketing-comunicacao.service';
import { PageHeader, EmptyState } from '../../components/Marketing';
import { ForwardToInbox } from '@mui/icons-material';

const STATUS_COLORS: Record<StatusCampanhaComunicacao, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  rascunho: 'default',
  agendada: 'info',
  a_enviar: 'warning',
  enviada: 'success',
  falhou: 'error',
  cancelada: 'default',
};

const PERFIS = [
  { codigo: 'PFCLI', label: 'Clientes' },
  { codigo: 'PFENT', label: 'Entidades/Comerciantes' },
  { codigo: 'PFAGT', label: 'Agentes' },
  { codigo: 'PFADM', label: 'Administradores' },
];

const steps = ['Segmentação', 'Conteúdo', 'Revisão'];

interface WizardState {
  nome: string;
  canal: CanalCampanha;
  filtros: FiltrosSegmentacao;
  template_sms_codigo: string;
  template_email_codigo: string;
  agendada_para: string;
}

const EMPTY_WIZARD: WizardState = {
  nome: '',
  canal: 'EMAIL',
  filtros: { apenas_opt_in: true, perfil_codigos: ['PFCLI'] },
  template_sms_codigo: '',
  template_email_codigo: '',
  agendada_para: '',
};

export default function Comunicacao() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CampanhaComunicacao[]>([]);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [w, setW] = useState<WizardState>(EMPTY_WIZARD);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [working, setWorking] = useState(false);
  const [createdId, setCreatedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await service.list());
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao carregar', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openWizard = () => {
    setW(EMPTY_WIZARD);
    setStep(0);
    setPreview(null);
    setCreatedId(null);
    setWizardOpen(true);
  };

  const toPayload = (): CampanhaPayload => ({
    nome: w.nome.trim(),
    canal: w.canal,
    filtros: w.filtros,
    template_sms_codigo: w.template_sms_codigo || undefined,
    template_email_codigo: w.template_email_codigo || undefined,
    agendada_para: w.agendada_para || undefined,
  });

  const next = async () => {
    if (step === 0) {
      if (!w.nome.trim()) {
        setToast({ msg: 'Nome obrigatório', severity: 'error' });
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (w.canal !== 'EMAIL' && !w.template_sms_codigo) {
        setToast({ msg: 'Código do template SMS obrigatório', severity: 'error' });
        return;
      }
      if (w.canal !== 'SMS' && !w.template_email_codigo) {
        setToast({ msg: 'Código do template Email obrigatório', severity: 'error' });
        return;
      }
      // Cria/atualiza rascunho e chama preview
      setWorking(true);
      try {
        let id = createdId;
        if (id == null) {
          const created = await service.create(toPayload());
          id = created.ID_CAMPANHA;
          setCreatedId(id);
        } else {
          await service.update(id, toPayload());
        }
        const p = await service.preview(id);
        setPreview(p);
        setStep(2);
      } catch (e: any) {
        setToast({ msg: e?.message || 'Erro ao gerar pré-visualização', severity: 'error' });
      } finally {
        setWorking(false);
      }
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const send = async () => {
    if (createdId == null) return;
    if (!window.confirm(`Disparar campanha para ${preview?.total_destinatarios ?? '?'} destinatários?`)) return;
    setWorking(true);
    try {
      await service.send(createdId);
      setToast({ msg: 'Envio iniciado em background', severity: 'success' });
      setWizardOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao enviar', severity: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminar campanha?')) return;
    try {
      await service.remove(id);
      setToast({ msg: 'Eliminada', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const cancel = async (id: number) => {
    try {
      await service.cancel(id);
      setToast({ msg: 'Cancelada', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const togglePerfil = (codigo: string) => {
    const curr = w.filtros.perfil_codigos || [];
    const next = curr.includes(codigo) ? curr.filter((c) => c !== codigo) : [...curr, codigo];
    setW({ ...w, filtros: { ...w.filtros, perfil_codigos: next } });
  };

  return (
    <Box>
      <PageHeader
        title="Campanhas SMS / Email"
        subtitle="Envio em massa com opt-in CMT e estimativa de custo"
        icon={<ForwardToInbox />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openWizard}>Nova Campanha</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Envios respeitam a flag <b>MARKETING_OPT_IN</b> no utilizador (CMT Cabo Verde). Utilizadores que pediram
        unsubscribe não recebem campanhas de marketing.
      </Alert>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Destinatários</TableCell>
                <TableCell align="right">Enviados</TableCell>
                <TableCell align="right">Falhou</TableCell>
                <TableCell align="right">Custo est. (CVE)</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<ForwardToInbox />}
                      title="Sem campanhas SMS/Email"
                      description="Crie a primeira campanha: escolha a segmentação, defina conteúdo via template e confirme depois de rever custo e destinatários."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openWizard}>
                          Nova Campanha
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c) => (
                  <TableRow key={c.ID_CAMPANHA} hover>
                    <TableCell>{c.NOME}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {(c.CANAL === 'SMS' || c.CANAL === 'AMBOS') && <SmsIcon fontSize="small" />}
                        {(c.CANAL === 'EMAIL' || c.CANAL === 'AMBOS') && <EmailIcon fontSize="small" />}
                        <Typography variant="caption">{c.CANAL}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={c.STATUS} color={STATUS_COLORS[c.STATUS]} />
                    </TableCell>
                    <TableCell align="right">{c.TOTAL_DESTINATARIOS}</TableCell>
                    <TableCell align="right">{c.TOTAL_ENVIADOS}</TableCell>
                    <TableCell align="right">{c.TOTAL_FALHOU}</TableCell>
                    <TableCell align="right">
                      {c.CUSTO_ESTIMADO_CVE != null ? Number(c.CUSTO_ESTIMADO_CVE).toLocaleString('pt-PT') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {(c.STATUS === 'rascunho' || c.STATUS === 'agendada') && (
                        <Tooltip title="Cancelar">
                          <IconButton size="small" onClick={() => cancel(c.ID_CAMPANHA)}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(c.STATUS === 'rascunho' || c.STATUS === 'agendada' || c.STATUS === 'cancelada') && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => remove(c.ID_CAMPANHA)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Wizard */}
      <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Nova Campanha</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {steps.map((s) => (
              <Step key={s}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                label="Nome da campanha"
                placeholder="Ex: Promo Black Friday SMS"
                value={w.nome}
                onChange={(e) => setW({ ...w, nome: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select
                  label="Canal"
                  value={w.canal}
                  onChange={(e) => setW({ ...w, canal: e.target.value as CanalCampanha })}
                >
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="AMBOS">SMS + Email</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Perfis alvo
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {PERFIS.map((p) => {
                    const active = (w.filtros.perfil_codigos || []).includes(p.codigo);
                    return (
                      <Chip
                        key={p.codigo}
                        label={p.label}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => togglePerfil(p.codigo)}
                        sx={{ mb: 1 }}
                      />
                    );
                  })}
                </Stack>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={w.filtros.apenas_opt_in !== false}
                    onChange={(e) =>
                      setW({ ...w, filtros: { ...w.filtros, apenas_opt_in: e.target.checked } })
                    }
                  />
                }
                label="Apenas utilizadores com opt-in (recomendado — CMT)"
              />
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {(w.canal === 'SMS' || w.canal === 'AMBOS') && (
                <TextField
                  fullWidth
                  label="Código do template SMS"
                  placeholder="Ex: promo_hamburguer"
                  helperText="O template deve existir em pg_sms_template"
                  value={w.template_sms_codigo}
                  onChange={(e) => setW({ ...w, template_sms_codigo: e.target.value })}
                />
              )}
              {(w.canal === 'EMAIL' || w.canal === 'AMBOS') && (
                <TextField
                  fullWidth
                  label="Código do template Email"
                  helperText="O template deve existir em pg_email_template e incluir link unsubscribe"
                  value={w.template_email_codigo}
                  onChange={(e) => setW({ ...w, template_email_codigo: e.target.value })}
                />
              )}
              <TextField
                fullWidth
                type="datetime-local"
                label="Agendar para (opcional)"
                InputLabelProps={{ shrink: true }}
                helperText="Deixe vazio para envio imediato"
                value={w.agendada_para}
                onChange={(e) => setW({ ...w, agendada_para: e.target.value })}
              />
            </Stack>
          )}

          {step === 2 && (
            <Box>
              {working ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : preview ? (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Total de destinatários
                        </Typography>
                        <Typography variant="h4" fontWeight={600}>
                          {preview.total_destinatarios}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Custo estimado
                        </Typography>
                        <Typography variant="h4" fontWeight={600} color="warning.main">
                          {preview.custo_estimado_cve.toLocaleString('pt-PT')} CVE
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      SMS
                    </Typography>
                    <Typography variant="h6">{preview.sms_count}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="h6">{preview.email_count}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Opt-out (excluídos)
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {preview.opt_out_skipped}
                    </Typography>
                  </Grid>
                  {preview.total_destinatarios === 0 && (
                    <Grid size={12}>
                      <Alert severity="warning">
                        Sem destinatários elegíveis com os filtros atuais.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="error">Pré-visualização indisponível</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizardOpen(false)} disabled={working}>
            Fechar
          </Button>
          {step > 0 && (
            <Button onClick={back} disabled={working}>
              Voltar
            </Button>
          )}
          {step < 2 && (
            <Button variant="contained" onClick={next} disabled={working}>
              {working ? 'A processar…' : 'Seguinte'}
            </Button>
          )}
          {step === 2 && preview && preview.total_destinatarios > 0 && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<SendIcon />}
              onClick={send}
              disabled={working}
            >
              Disparar Campanha
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
